import React, { createContext, useContext } from "react";
import { type AccountConfig, DEFAULT_ACCOUNT_CONFIG } from "./accountConfig";

/**
 * Shared context carrying the full {@link AccountConfig} to learner-facing
 * surfaces. Both the authoring preview (resolved from the selected client
 * context via author-api) and exported SCORM/CAP players provide a value here,
 * so account-level settings are consumed from a single seam via
 * {@link useAccountConfig}.
 */
const AccountConfigContext = createContext<AccountConfig>(DEFAULT_ACCOUNT_CONFIG);
export type AccountConfigResolutionStatus = "loading" | "ready" | "error";
const AccountConfigStatusContext = createContext<AccountConfigResolutionStatus>("ready");
const AccountConfigScopeContext = createContext<string>("default");

export interface AccountConfigProviderProps {
  config: AccountConfig;
  status?: AccountConfigResolutionStatus;
  /** Stable selected-account identity, independent of the asynchronously loaded config payload. */
  scopeKey?: string;
  children: React.ReactNode;
}

export function AccountConfigProvider({
  config,
  status = "ready",
  scopeKey = "default",
  children,
}: AccountConfigProviderProps) {
  return (
    <AccountConfigContext.Provider value={config}>
      <AccountConfigStatusContext.Provider value={status}>
        <AccountConfigScopeContext.Provider value={scopeKey}>
          {children}
        </AccountConfigScopeContext.Provider>
      </AccountConfigStatusContext.Provider>
    </AccountConfigContext.Provider>
  );
}

/**
 * Reads the current account config. Returns {@link DEFAULT_ACCOUNT_CONFIG} when
 * no provider is mounted, so isolated components and tests get safe defaults
 * rather than throwing.
 */
export function useAccountConfig(): AccountConfig {
  return useContext(AccountConfigContext);
}

/**
 * Whether question results are hidden from the learner. The account-level
 * "Hide Results from Learners" setting takes precedence over the block's own
 * `hideResults` value, applied at render time so stored blocks are never rewritten.
 * Fails closed: until the account config has resolved (or if it failed to load)
 * the setting is unknown, so results stay hidden.
 */
export function useResultsHidden(blockHideResults: boolean | undefined): boolean {
  const { hideResultsFromLearners } = useAccountConfig();
  const status = useAccountConfigStatus();
  return status !== 'ready' || hideResultsFromLearners || blockHideResults === true;
}

export function useAccountConfigStatus(): AccountConfigResolutionStatus {
  return useContext(AccountConfigStatusContext);
}

/** Reads the stable selected-account scope for launch-owned state such as pacing. */
export function useAccountConfigScope(): string {
  return useContext(AccountConfigScopeContext);
}
